import {A_RATE, K_RATE} from 'constants/automation-rates.js';
import Tone from 'tone';
import KeyScaling from './KeyScaling.js';
import {LFO, ENVELOPE} from 'constants/mod-types.js';
import ParameterMod from './ParameterMod.js';
//import {AudioParamValueParameter} from './AbstractAudioParamParameter.js';

export default superclass => class extends superclass  {
    connectToLFO(lfo) {
        let createdDepth = false;
        if (!this.depthParameter) {
            this.initDepth();
            createdDepth = true;
        }

        if (this.isConnectedToLFO(lfo)) {
            // already connected
            return undefined;
        }
        this.lfos = [... this.lfos, lfo];

        if (this.audioParams) {
            this.audioParams.forEach(
                (audioParam, i) =>
                    lfo.connect(audioParam, this, i));
            this.connectToDepth(lfo, undefined, createdDepth);
            return new ParameterMod(
                this,
                lfo,
                undefined,
                this.audioParams.map(
                    (x, i) => 
                        lfo.getScaleForParameter(this, i)));
           
        } else {
            this.automationRate = A_RATE;
            lfo.connect(this.audioParam, this, 0);
            this.connectToDepth(lfo, undefined, createdDepth);
            return new ParameterMod(
                this,
                lfo,
                lfo.getScaleForParameter(this),
                undefined,
                LFO);
        } 
    }

    connectToEnvelope(envelope) {
        let createdDepth = false;
        if (!this.depthParameter) {
            this.initDepth();
            createdDepth = true;
        }

        if (this.isConnectedToEnvelope(envelope)) {
            // already connected
            return undefined;
        }
        this.envelopes = [... this.envelopes, envelope];
        if (this.audioParams) {
            this.audioParams.forEach(
                (audioParam, i) =>
                    envelope.connect(audioParam, this, i));
            this.connectToDepth(undefined, envelope, createdDepth);
            return new ParameterMod(
                this,
                    envelope,
                    undefined,
                this.audioParams.map(
                    (x, i) => 
                        envelope.getScaleForParameter(this, i)),
                ENVELOPE);
        } else {
            this.automationRate = A_RATE;
            envelope.connect(this.audioParam, this, 0);
            this.connectToDepth(undefined, envelope, createdDepth);
            return new ParameterMod(
                this,
                envelope,
                envelope.getScaleForParameter(this),
                undefined,
                ENVELOPE);
        } 
    }

    enableKeyScaling() {
        this.keyScaling = new KeyScaling(this);
    }

    // Every parameter has a depth that controls how
    // strong EVERY modulator affects it

    _depth = 1; 
    
    get depth() {
        return this.depthParameter ?
            this.depthParameter.get() : this._depth;
    }

    set depth(x) {
        this._depth = x;
        if (this.depthParameter) {
            this.depthParameter.set(x, false, true);
        } 
    }

    connectToDepth(lfo, env, createdDepth) {
        if (!this.depthParameter.audioParam) {
            return;
        }

        let mod = lfo || env;
        if (mod) {
            // get the depth signal from the modulator we just added
            let depthSignal = mod.getDepthForParameter(this);

            if (!createdDepth) {
                let depth = this.depth;
                /*
                if (!Tone.Master.context.isOffline) {
                    // in order to avoid terrifying pops, ramp the depth to 0 and then
                    // back up to _depth 
                    let time = Tone.Master.context.currentTime;
                    if (!isNaN(depth)) {
                        // this.depthParameter.audioParam.rampTo(0, 0.01, time);
                        this.depthParameter.audioParam.rampTo(depth, 0.2, time);
                    }
                } else {
                */
                if (!isNaN(depth)) {
                    this.depthParameter.audioParam.value = depth;
                }
            }
            
            this.depthParameter.audioParam.connect(depthSignal.gain);
        }
    }

    disconnectFromDepth(lfo, env) {
        if (!this.depthParameter.audioParam) {
            return;
        }

        let mod = lfo || env;
        if (mod) {
            let depthSignal = mod.getDepthForParameter(this);
            this.depthParameter.audioParam.disconnect(depthSignal.gain);
        }
    }

    disconnectFromLFO(lfo, deleteAudioParam=true, disconnectFromDepth=true) {
        let i = this.lfos.indexOf(lfo);
        if (i < 0) {
            return;
        }

        if (this.useMeter) {
            this.useMeter = false;
        } else if (this.audioParams) {
            if (disconnectFromDepth) {
                this.disconnectFromDepth(lfo);
            }
            this.audioParams.forEach((audioParam, voiceNumber) =>
                lfo.disconnect(audioParam, this, voiceNumber));
        } else if (this.audioParam) {
            if (disconnectFromDepth) {
                this.disconnectFromDepth(lfo);
            }
            lfo.disconnect(this.audioParam, this, 0);
        }
        this.lfos.splice(i, 1);
        this.lfos = [... this.lfos];

        if (this.lfos.length === 0 && deleteAudioParam) {
            let audioParam = this.audioParam;
            let meter = this.meter;
            this.audioParam = undefined;
            this.meter = undefined;
        }

        this.cleanDepthIfNecessary();
    }

    disconnectFromEnvelope(envelope, deleteAudioParam=true, disconnectFromDepth=true) {
        let i = this.envelopes.indexOf(envelope);
        if (i < 0) {
            return;
        }

        if (this.useMeter) {
            this.useMeter = false;
        } else if (this.audioParams) {
            if (disconnectFromDepth) {
                this.disconnectFromDepth(undefined, envelope);
            }
            this.audioParams.forEach((audioParam, i) =>
                envelope.disconnect(audioParam, this, i));
        } else {
            if (disconnectFromDepth) {
                this.disconnectFromDepth(undefined, envelope);
            }
            this.automationRate = A_RATE;
            envelope.disconnect(this.audioParam, this, 0);
        }
        this.envelopes.splice(i, 1);
        this.envelopes = [... this.envelopes];
        this.cleanDepthIfNecessary();
    }

    disableKeyScaling() {
        this.keyScaling = undefined;
        this.set(this.get(true), true, true);
    }

    disconnectFromAllModulators() {
        let lfos = [... this.lfos]
        for (let i in lfos) {
            this.disconnectFromLFO(lfos[i], false, false);
        } 
        let envelopes = [... this.envelopes];
        for (let i in envelopes) {
            this.disconnectFromEnvelope(envelopes[i], undefined, false);
        } 

        this.lfos = [];
        this.envelopes = [];
        if (this.keyScaling) {
            this.disableKeyScaling();
        }
    }

    getParameterMod(lfo, envelope) {
        let mod = lfo || envelope;
        if (this.audioParams) {
            return new ParameterMod(
                this,
                mod,
                undefined,
                this.audioParams.map(
                    (x, i) => 
                        mod.getScaleForParameter(this, i)),
                lfo ? LFO : ENVELOPE
            );
        } else {
            return new ParameterMod(
                this,
                mod,
                mod.getScaleForParameter(this),
                undefined,
                lfo ? LFO : ENVELOPE);
        }
    }

    get parameterMods() {
        if (this.audioParams !== undefined) {
            let lfoMods = this.lfos.map(
                lfo => new ParameterMod(
                    this,
                    lfo,
                    undefined,
                    this.audioParams.map((x, i) => 
                       lfo.getScaleForParameter(this, i))));
            let envelopeMods = this.envelopes.map(
                env => new ParameterMod(
                    this,
                    env,
                    undefined,
                    this.audioParams.map((x, i) => 
                       env.getScaleForParameter(this, i)),
                    ENVELOPE));

            return lfoMods.concat(envelopeMods).filter(
                x =>
                    x.scale !== undefined);
        } 

        let lfoMods = this.lfos.map(
            lfo => new ParameterMod(
                this,
                lfo,
                lfo.getScaleForParameter(this),
                undefined,
                LFO));

        let envMods = this.envelopes.map(
            env => new ParameterMod(
                this,
                env,
                env.getScaleForParameter(this),
                undefined,
                ENVELOPE));

        return lfoMods.concat(envMods).filter(x => x.scale !== undefined);
    }

    getConnectableParam() {
        return this.audioParam;
    }

    isAudioParamEnabled() {
        return this.audioParam !== undefined ||
            this.audioParams !== undefined;
    }

    isConnectedToAnyModulator(includeKeyScaling=true) {
        return this.isConnectedToAnyLFO() ||
            this.isConnectedToAnyEnvelope() ||
            (includeKeyScaling && this.keyScaling);
    }

    isConnectedToAnyLFO() {
        return this.lfos && this.lfos.length > 0;
    }

    isConnectedToLFO(lfo) {
        return this.lfos && this.lfos.includes(lfo);
    }

    isConnectedToAnyEnvelope() {
        return this.envelopes && this.envelopes.length > 0;
    }

    isConnectedToEnvelope(envelope) {
        return this.envelopes && this.envelopes.includes(envelope);
    }

    enableAudioParam() {
        if (!this.audioParam && !this.audioParams) {
            this.useMeter = true;
        }
    }

    setAudioParam(audioParam) {
        this.audioParam = audioParam;
        this.automationRate = A_RATE;
        this.useMeter = false;
    }

    setAudioParams(audioParams) {
        this.audioParams = audioParams;
        this.automationRate = A_RATE;
        this.useMeter = false;
    }

    // loads the parameter mods from the json
    loadModsFromJson(json) {
        if (json.mods !== undefined) {
            for (let i in json.mods) {
                let jsonMod = json.mods[i];
                let modulator = jsonMod.modulator || jsonMod.lfo;
                let jsonModType = jsonMod.type || LFO;
                let parameterMods = this.parameterMods;
                for (let j in parameterMods) {
                    let mod = parameterMods[j];
                    if (mod.modulator.number === modulator &&
                        mod.type === jsonModType) {
                        let scales = mod.scales || [mod.scale];
                        if (!Tone.Master.context.isOffline) {
                            scales.forEach(x => x.rampTime = 1);
                        }
                        mod.min = parseFloat(jsonMod.min);
                        mod.max = parseFloat(jsonMod.max);
                        if (!Tone.Master.context.isOffline) {
                            scales.forEach(s => s.rampTime = 0.001);
                        }
                        break;
                    }
                }
            }
        }
    }

    getMeterValue(depth) {
        let sum = 0;
        for (let i in this.lfos) {
            let scaler = this.lfos[i].getScaleForParameter(this);
            if (scaler === undefined) {
                continue;
            }
            if (depth >= 8) {
                continue;
            }
            let value = this.lfos[i].getValue(depth) * this.depth;
            let max = scaler.max;
            let min = scaler.min;
            let scaled = (max - min) * (value + 100) / 200 + min;
            let otherScaled = (scaler.max - scaler.min) * (value + 100) / 200 + scaler.min;
            sum += scaled;
        }
        for (let i in this.envelopes) {
            let scaler = this.envelopes[i].getScaleForParameter(this);
            if (!scaler) {
                continue;
            }
            let value = this.envelopes[i].getValue(this.trackStep);
            let max = scaler.max;
            let min = scaler.min;
            let scaled = (max - min) * value / 1 + min;
            sum += scaled;
        }
        
        return sum;
   };

    getKeyScaledValue(transpose, force=false) {
        let minTranspose = -32;
        let maxTranspose = 60;
        let transposeRatio = (transpose - minTranspose) / (maxTranspose - minTranspose);

        if (this.keyScaling === undefined || transpose === undefined) {
            return this.get(force);
        }
        let v = this.get(force) + this.keyScaling.min + (this.keyScaling.max - this.keyScaling.min) * transposeRatio;
        return v;
    }

    cleanDepthIfNecessary() {
        if (this.isConnectedToAnyModulator() || !this.depthSignal) {
            return;
        }

        // we are not connected to any modulator so we can dispose
        // of the depth
        this.depthSignal.value = 0;
        this.depthSignal.disconnect();
        this.depthParameter.dispose(true);
        this.depthParameter = undefined;
        this.depthSignal = undefined;
    }

    resetMinMax() {
        this.min = this.originalMin;
        this.max = this.originalMax;
    }
}
